'use client';

import React from 'react';

type ZodxState = 'ready' | 'active' | 'paused' | 'warning' | 'stopped';

interface ZodxOrbHeroProps {
  zodxState?: ZodxState;
  size?: number;
}

const stateConfig: Record<ZodxState, {
  label: string;
  color: string;
  glowColor: string;
  orbitColor: string;
  pulseClass: string;
  bgGradient: string;
  dotColor: string;
}> = {
  ready: {
    label: 'ZODX Ready',
    color: '#3B82F6',
    glowColor: 'rgba(59,130,246,0.4)',
    orbitColor: 'rgba(59,130,246,0.5)',
    pulseClass: 'orb-pulse-ready',
    bgGradient: 'radial-gradient(circle at 35% 35%, #1D4ED8, #0A0F1E 70%)',
    dotColor: '#60A5FA',
  },
  active: {
    label: 'ZODX Active',
    color: '#10B981',
    glowColor: 'rgba(16,185,129,0.4)',
    orbitColor: 'rgba(16,185,129,0.5)',
    pulseClass: 'orb-pulse-active',
    bgGradient: 'radial-gradient(circle at 35% 35%, #065F46, #0A0F1E 70%)',
    dotColor: '#34D399',
  },
  paused: {
    label: 'ZODX Paused',
    color: '#F59E0B',
    glowColor: 'rgba(245,158,11,0.3)',
    orbitColor: 'rgba(245,158,11,0.4)',
    pulseClass: '',
    bgGradient: 'radial-gradient(circle at 35% 35%, #78350F, #0A0F1E 70%)',
    dotColor: '#FCD34D',
  },
  warning: {
    label: 'ZODX Warning',
    color: '#F59E0B',
    glowColor: 'rgba(245,158,11,0.5)',
    orbitColor: 'rgba(245,158,11,0.6)',
    pulseClass: 'orb-pulse-warning',
    bgGradient: 'radial-gradient(circle at 35% 35%, #78350F, #0A0F1E 70%)',
    dotColor: '#FCD34D',
  },
  stopped: {
    label: 'ZODX Stopped',
    color: '#64748B',
    glowColor: 'rgba(100,116,139,0.2)',
    orbitColor: 'rgba(100,116,139,0.3)',
    pulseClass: '',
    bgGradient: 'radial-gradient(circle at 35% 35%, #1E293B, #0A0F1E 70%)',
    dotColor: '#94A3B8',
  },
};

export default function ZodxOrbHero({ zodxState = 'ready', size = 160 }: ZodxOrbHeroProps) {
  const safeState: ZodxState = stateConfig[zodxState] ? zodxState : 'ready';
  const config = stateConfig[safeState];

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Orb container */}
      <div className="relative" style={{ width: size, height: size }}>
        {/* Outer ring glow */}
        <div
          className={`absolute inset-0 rounded-full ${config.pulseClass}`}
          style={{ background: 'transparent' }}
        />

        {/* Orbit ring */}
        <div
          className="absolute inset-0 rounded-full border orb-rotate"
          style={{
            borderColor: 'transparent',
            borderTopColor: config.orbitColor,
            borderRightColor: config.orbitColor,
            opacity: 0.6,
          }}
        />

        {/* Secondary orbit — slower, opposite */}
        <div
          className="absolute rounded-full border"
          style={{
            inset: size * 0.08,
            borderColor: 'transparent',
            borderBottomColor: config.color,
            borderLeftColor: config.color,
            opacity: 0.3,
            animation: 'orb-rotate 18s linear infinite reverse',
          }}
        />

        {/* Core orb */}
        <div
          className={`absolute rounded-full ${config.pulseClass}`}
          style={{
            inset: size * 0.12,
            background: config.bgGradient,
            border: `1px solid ${config.color}40`,
          }}
        >
          {/* Inner highlight */}
          <div
            className="absolute rounded-full"
            style={{
              top: '15%',
              left: '15%',
              width: '30%',
              height: '30%',
              background: `radial-gradient(circle, ${config.dotColor}50 0%, transparent 80%)`,
            }}
          />

          {/* Z logo center */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="font-bold font-mono select-none"
              style={{
                fontSize: size * 0.22,
                color: config.color,
                textShadow: `0 0 20px ${config.glowColor}`,
              }}
            >
              Z
            </span>
          </div>
        </div>

        {/* Floating dots */}
        {[0, 60, 120, 180, 240, 300].map((deg, i) => {
          const rad = (deg * Math.PI) / 180;
          const r = size * 0.46;
          const cx = size / 2 + r * Math.cos(rad);
          const cy = size / 2 + r * Math.sin(rad);
          return (
            <div
              key={`orb-dot-${deg}`}
              className="absolute rounded-full"
              style={{
                width: i % 2 === 0 ? 4 : 3,
                height: i % 2 === 0 ? 4 : 3,
                background: config.dotColor,
                left: cx - (i % 2 === 0 ? 2 : 1.5),
                top: cy - (i % 2 === 0 ? 2 : 1.5),
                opacity: i % 2 === 0 ? 0.9 : 0.5,
              }}
            />
          );
        })}
      </div>

      {/* State label */}
      <div
        className="status-badge"
        style={{
          background: `${config.color}18`,
          color: config.color,
          border: `1px solid ${config.color}35`,
        }}
      >
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: config.color }} />
        {config.label}
      </div>
    </div>
  );
}